// ====================================
// File: ExperienceSection.jsx
// ====================================
import React from "react";
import Icons from "./Icons";
import { PLACEHOLDER_TEXT } from "./constants";

const ExperienceSection = ({
  section,
  location,
  updateSectionItem,
  removeSectionItem,
  addSectionItem,
}) => {
  // MODIFICATION: Made this function resilient.
  // It now defaults to an empty object (`|| {}`) if a specific placeholder isn't found.
  // This is the key fix that prevents the "Cannot read properties of undefined" crash.
  const getPlaceholders = () => {
    switch (section.type) {
      case "experience":
        return PLACEHOLDER_TEXT.experience || {};
      case "volunteer":
        return PLACEHOLDER_TEXT.volunteer || {};
      case "projects":
        return PLACEHOLDER_TEXT.projects || {};
      default:
        return PLACEHOLDER_TEXT.experience || {};
    }
  };

  const getLabels = () => {
    switch (section.type) {
      case "experience":
        return {
          title: "Job Title",
          organization: "Company Information",
          details: "Responsibilities & Achievements",
          addButton: "Add Experience",
        };
      case "volunteer":
        return {
          title: "Role",
          organization: "Organization",
          details: "Activities & Impact",
          addButton: "Add Volunteer Role",
        };
      case "projects":
        return {
          title: "Project Title",
          organization: "Technologies Used",
          details: "Project Description",
          addButton: "Add Project",
        };
      default:
        return {
          title: "Title",
          organization: "Organization",
          details: "Details",
          addButton: "Add Item",
        };
    }
  };

  const placeholders = getPlaceholders();
  const labels = getLabels();

  const getEmptyItem = () => {
    switch (section.type) {
      case "experience":
        return { jobTitle: "", companyInfo: "", details: "" };
      case "volunteer":
        return { role: "", organization: "", details: "" };
      case "projects":
        return { title: "", technologies: "", description: "" };
      default:
        return { title: "", organization: "", details: "" };
    }
  };

  return (
    <div className="experience-section">
      {section.items?.map((item) => (
        <div key={item.id} className="experience-item">
          <button
            onClick={() => removeSectionItem(item.id)}
            className="item-remove-button"
            title="Remove item"
          >
            <Icons.X className="w-4 h-4" />
          </button>

          <div className="item-content">
            <div className="form-row">
              <div className="form-group flex-1">
                <label className="form-label">{labels.title}</label>
                <input
                  type="text"
                  value={item.jobTitle || item.role || item.title || ""}
                  onChange={(e) =>
                    updateSectionItem(item.id, {
                      [section.type === "experience"
                        ? "jobTitle"
                        : section.type === "volunteer"
                        ? "role"
                        : "title"]: e.target.value,
                    })
                  }
                  className="form-input"
                  placeholder={
                    section.type === "projects"
                      ? placeholders.title
                      : section.type === "volunteer"
                      ? placeholders.role
                      : placeholders.jobTitle
                  }
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{labels.organization}</label>
              <input
                type="text"
                value={
                  item.companyInfo ||
                  item.organization ||
                  item.technologies ||
                  ""
                }
                onChange={(e) =>
                  updateSectionItem(item.id, {
                    [section.type === "experience"
                      ? "companyInfo"
                      : section.type === "volunteer"
                      ? "organization"
                      : "technologies"]: e.target.value,
                  })
                }
                className="form-input"
                placeholder={
                  section.type === "projects"
                    ? placeholders.technologies
                    : section.type === "volunteer"
                    ? placeholders.organization
                    : placeholders.companyInfo
                }
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                {labels.details}
                <span className="label-hint">
                  Use bullet points (•) to list key achievements
                </span>
              </label>
              <textarea
                value={item.details || item.description || ""}
                onChange={(e) =>
                  updateSectionItem(item.id, {
                    [section.type !== "projects" ? "details" : "description"]:
                      e.target.value,
                  })
                }
                placeholder={
                  section.type === "projects"
                    ? placeholders.description
                    : placeholders.details
                }
                className="form-textarea"
                rows={5}
              />
            </div>
          </div>
        </div>
      ))}

      <button
        onClick={() => addSectionItem(getEmptyItem())}
        className="add-item-button"
      >
        <Icons.Plus className="w-4 h-4" />
        {labels.addButton}
      </button>

      {section.items?.length === 0 && (
        <p className="empty-message">
          No{" "}
          {section.type === "projects"
            ? "projects"
            : section.type === "volunteer"
            ? "volunteer experiences"
            : "work experiences"}{" "}
          added yet. Click the button above to add your first entry.
        </p>
      )}
    </div>
  );
};

export default ExperienceSection;
